import { autoUpdate, flip, FloatingPortal, offset, useFloating } from '@floating-ui/react'
import ctl from '@netlify/classnames-template-literals'
import { ChevronDownIcon } from 'lucide-react'
import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { clampNumber, roundToStep } from '../support/number-step'

export type ComplexNumericMode<T extends string = string> = {
	id: T
	label: string
	numeric: boolean
}

export type WidgetInputComplexNumericFieldProps<T extends string = string> = {
	value: number
	onChange: (value: number) => void
	onTransientChange?: (value: number) => void
	label?: string
	min?: number
	max?: number
	step?: number
	/** Rounding granularity for committed values. Defaults to `step`. Use to allow decimals without shrinking the keyboard/drag step. */
	precision?: number
	suffix?: string
	sensitivity?: number
	defaultValue?: number
	innerLabel?: string
	modes?: ComplexNumericMode<T>[]
	mode?: T
	onModeChange?: (mode: T) => void
}

export const WidgetInputComplexNumericField = memo(function WidgetInputComplexNumericField<T extends string = string>({
	value,
	onChange,
	onTransientChange,
	label,
	min,
	max,
	step = 1,
	precision,
	suffix,
	sensitivity = 0.4,
	defaultValue = 0,
	innerLabel,
	modes,
	mode,
	onModeChange,
}: WidgetInputComplexNumericFieldProps<T>) {
	const [displayValue, setDisplayValue] = useState(String(value))
	const [isFocused, setIsFocused] = useState(false)
	const [isDragging, setIsDragging] = useState(false)
	const [dropdownOpen, setDropdownOpen] = useState(false)

	const inputRef = useRef<HTMLInputElement>(null)
	const dragState = useRef({
		startY: 0,
		startValue: 0,
		accumulator: 0,
		hasMoved: false,
		pointerId: null as number | null,
		lastValue: 0,
	})

	const { refs, floatingStyles } = useFloating({
		open: dropdownOpen,
		onOpenChange: setDropdownOpen,
		placement: 'bottom-end',
		middleware: [offset(4), flip()],
		whileElementsMounted: autoUpdate,
	})

	const currentMode = modes?.find((m) => m.id === mode)
	const isNumeric = !currentMode || currentMode.numeric
	const isDirty = value !== defaultValue
	const showReset = isDirty && isNumeric && !isDragging

	// Sync display value when external value changes while not focused
	useEffect(() => {
		if (!isFocused) {
			setDisplayValue(String(value))
		}
	}, [value, isFocused])

	// Close dropdown on outside click
	useEffect(() => {
		if (!dropdownOpen) { return }
		const handleClick = (e: MouseEvent) => {
			const reference = refs.reference.current
			const floating = refs.floating.current
			if (
				reference instanceof HTMLElement && reference.contains(e.target as Node)
				|| floating instanceof HTMLElement && floating.contains(e.target as Node)
			) { return }
			setDropdownOpen(false)
		}
		document.addEventListener('pointerdown', handleClick)
		return () => document.removeEventListener('pointerdown', handleClick)
	}, [dropdownOpen, refs.reference, refs.floating])

	const roundingStep = precision ?? step

	const commit = useCallback(
		(v: number): number => {
			const clamped = clampNumber(roundToStep(v, roundingStep), min, max)
			onChange(clamped)
			return clamped
		},
		[roundingStep, min, max, onChange],
	)

	const reset = useCallback(() => {
		const v = commit(defaultValue)
		setDisplayValue(String(v))
	}, [commit, defaultValue])

	// --- Input handlers (only active when numeric) ---

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setDisplayValue(e.target.value)
		const parsed = parseFloat(e.target.value)
		if (!isNaN(parsed)) { commit(parsed) }
	}

	const handleFocus = () => {
		setIsFocused(true)
		setDisplayValue(String(value))
		setTimeout(() => inputRef.current?.select(), 0)
	}

	const handleBlur = () => {
		setIsFocused(false)
		const result = commit(value)
		setDisplayValue(String(result))
	}

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
			e.preventDefault()
			const mult = e.shiftKey ? 10 : e.altKey ? 0.1 : 1
			const delta = (e.key === 'ArrowUp' ? 1 : -1) * step * mult
			const next = commit(value + delta)
			setDisplayValue(String(next))
		}
		if (e.key === 'Enter') { inputRef.current?.blur() }
		if (e.key === 'Escape') {
			reset()
			inputRef.current?.blur()
		}
	}

	// Reset body styles if the component unmounts mid-drag
	useEffect(() => {
		return () => {
			if (dragState.current.pointerId != null) {
				try {
					inputRef.current?.releasePointerCapture(dragState.current.pointerId)
				} catch { /* noop */ }
				dragState.current.pointerId = null
				dragState.current.hasMoved = false
				document.body.style.cursor = ''
				document.body.style.userSelect = ''
			}
		}
	}, [])

	// --- Drag-to-scrub handlers ---

	const startDrag = (e: React.PointerEvent) => {
		e.preventDefault()
		inputRef.current?.setPointerCapture(e.pointerId)
		dragState.current = {
			startY: e.clientY,
			startValue: value,
			accumulator: 0,
			hasMoved: false,
			pointerId: e.pointerId,
			lastValue: value,
		}
	}

	const handlePointerDown = (e: React.PointerEvent) => {
		if (isFocused) { return }
		startDrag(e)
	}

	const handlePointerMove = (e: React.PointerEvent) => {
		if (dragState.current.pointerId == null) { return }
		const dy = e.clientY - dragState.current.startY
		if (!dragState.current.hasMoved && Math.abs(dy) < 3) { return }
		if (!dragState.current.hasMoved) {
			dragState.current.hasMoved = true
			setIsDragging(true)
			document.body.style.cursor = 'ns-resize'
			document.body.style.userSelect = 'none'
		}
		dragState.current.accumulator += e.movementY * -1
		const rawDelta = dragState.current.accumulator * sensitivity * step
		const clamped = clampNumber(roundToStep(dragState.current.startValue + rawDelta, roundingStep), min, max)
		dragState.current.lastValue = clamped
		;(onTransientChange ?? onChange)(clamped)
		setDisplayValue(String(clamped))
	}

	const handlePointerUp = (e: React.PointerEvent) => {
		if (dragState.current.pointerId == null) { return }
		try {
			inputRef.current?.releasePointerCapture(e.pointerId)
		} catch { /* noop */ }
		const hadMoved = dragState.current.hasMoved
		dragState.current.pointerId = null
		dragState.current.hasMoved = false
		if (isDragging) {
			setIsDragging(false)
			document.body.style.cursor = ''
			document.body.style.userSelect = ''
		}
		if (hadMoved && onTransientChange) {
			onChange(dragState.current.lastValue)
		}
		if (!hadMoved) { inputRef.current?.focus() }
	}

	return (
		<div className="relative inline-grid min-w-0">
			<div className="relative flex items-stretch">
				<div className="relative flex-1">
					{innerLabel && (
						<span className="pointer-events-none absolute left-2 top-1/2 -translate-y-[calc(50%-0.5px)] font-mono text-[10px] font-medium text-grey-400 select-none">
							{innerLabel}
						</span>
					)}
					<input
						ref={inputRef}
						type="text"
						inputMode="decimal"
						value={isNumeric ? displayValue : 'Auto'}
						onChange={isNumeric ? handleInputChange : undefined}
						onFocus={isNumeric ? handleFocus : undefined}
						onBlur={isNumeric ? handleBlur : undefined}
						onKeyDown={isNumeric ? handleKeyDown : undefined}
						onPointerDown={isNumeric ? handlePointerDown : undefined}
						onPointerMove={isNumeric ? handlePointerMove : undefined}
						onPointerUp={isNumeric ? handlePointerUp : undefined}
						disabled={!isNumeric}
						aria-label={label}
						className={ctl(`
							w-full tabular-nums font-mono text-sm
							py-1.5 pr-8
							${innerLabel ? 'pl-6' : 'px-2.5'}
							${modes ? 'rounded-l-md' : 'rounded-md'}
							border outline-none
							transition-[border-color,box-shadow] duration-150
							${
							isFocused
								? 'border-blue-500 ring-2 ring-blue-500/15 bg-white'
								: isDragging
								? 'border-blue-400 ring-2 ring-blue-400/10 bg-grey-50'
								: 'border-grey-300 bg-white'
						}
							${
							isNumeric
								? (isFocused ? 'cursor-text' : 'cursor-ns-resize')
								: 'cursor-default text-grey-400 italic'
						}
							${!isFocused ? 'caret-transparent' : ''}
						`)}
					/>
					<div className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
						<button
							type="button"
							tabIndex={-1}
							onPointerDown={(e) => e.stopPropagation()}
							onClick={(e) => {
								e.stopPropagation()
								reset()
							}}
							className={ctl(`
								pointer-events-auto
								flex h-5 items-center justify-center rounded
								bg-transparent text-grey-400
								cursor-pointer
								hover:bg-grey-100 hover:text-grey-500
								transition-all duration-150 overflow-hidden
								${showReset ? 'w-5 opacity-100' : 'w-0 opacity-0 pointer-events-none'}
							`)}
							title={`Reset to ${defaultValue}`}
						>
							<svg
								className="shrink-0"
								width="10"
								height="10"
								viewBox="0 0 10 10"
								fill="none"
								stroke="currentColor"
								strokeWidth="1.5"
								strokeLinecap="round"
							>
								<path d="M2 8L8 2M8 8L2 2" />
							</svg>
						</button>
						{suffix && !modes && (
							<span className="pointer-events-none font-mono text-xs text-grey-400 px-2">
								{suffix}
							</span>
						)}
					</div>
				</div>
				{modes && (
					<button
						ref={refs.setReference}
						type="button"
						onClick={() => setDropdownOpen(!dropdownOpen)}
						className={ctl(`
							flex items-center gap-1 px-2.5
							border border-l-0 border-grey-300
							rounded-r-md
							bg-grey-50 text-xs font-semibold text-grey-600
							cursor-pointer
							hover:bg-grey-100
							transition-colors duration-150
							min-w-14 justify-center
						`)}
					>
						{currentMode?.label}
						<ChevronDownIcon
							className={ctl(`
								size-3 transition-transform duration-150
								${dropdownOpen ? 'rotate-180' : ''}
							`)}
						/>
					</button>
				)}
				{dropdownOpen && modes && (
					<FloatingPortal>
						<div
							ref={refs.setFloating}
							style={floatingStyles}
							className="z-50 min-w-28 overflow-hidden rounded-md border border-grey-200 bg-white shadow-lg"
						>
							{modes.map((m) => (
								<button
									key={m.id}
									type="button"
									onClick={() => {
										onModeChange?.(m.id)
										setDropdownOpen(false)
									}}
									className={ctl(`
										block w-full cursor-pointer border-none px-3 py-2 text-left text-sm
										transition-colors duration-100
										${
										m.id === mode
											? 'bg-grey-100 text-grey-900'
											: 'bg-transparent text-grey-600 hover:bg-grey-50'
									}
									`)}
								>
									{m.label}
								</button>
							))}
						</div>
					</FloatingPortal>
				)}
			</div>
		</div>
	)
}) as <T extends string = string>(props: WidgetInputComplexNumericFieldProps<T>) => React.ReactNode
